import jwtDecode from 'jwt-decode'
import ApolloClient from 'apollo-client'

import {
  GetClientCurrencyDocument,
  GetClientCurrencyQuery,
  GetClientCurrencyQueryVariables,
  QueryHookOptions,
  useGetClientCurrencyQuery,
} from '@sketch/gql-types'
import { getParsedItem } from '@sketch/utils'

import { localStorageKeys } from '@sketch/constants'
import { ErrorHandler } from '@sketch/tracing'

type JWT = {
  exp: number
}

const decodeToken = (token?: string) => {
  if (token) {
    try {
      return jwtDecode<JWT>(token)
    } catch (e) {
      // There was an error decoding the token, it might be invalid or malformed
      ErrorHandler.shouldNeverHappen(`Issues decoding currency token ${e}`)
    }
  }

  return undefined
}

const validateToken = (token?: string) => {
  const tokenDecoded = decodeToken(token)
  const isTokenValid = tokenDecoded && tokenDecoded?.exp > Date.now() / 1000

  if (isTokenValid) {
    return token
  }

  return undefined
}

const getValidToken = () => {
  const currencyToken = getParsedItem(localStorageKeys.currencyToken)
  const tokenValid = validateToken(currencyToken)

  if (!tokenValid) {
    localStorage.removeItem(localStorageKeys.currencyToken)
  }

  return tokenValid
}

export const getClientCurrencyQuery = (apolloClient: ApolloClient<any>) => {
  const currencyToken = getValidToken()

  return apolloClient.query<
    GetClientCurrencyQuery,
    GetClientCurrencyQueryVariables
  >({
    query: GetClientCurrencyDocument,
    fetchPolicy: 'network-only',
    variables: { token: currencyToken },
  })
}

export const useGetClientCurrency = (
  options?: QueryHookOptions<
    GetClientCurrencyQuery,
    GetClientCurrencyQueryVariables
  >
) => {
  const currencyToken = getValidToken()

  return useGetClientCurrencyQuery({
    ...options,
    variables: { token: currencyToken },
  })
}
